import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Watch, Tag, FolderTree, ShoppingCart, Users, LogOut, Menu, X, MessageSquare, Settings, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import { messagingAPI } from '../utils/api';

const Layout = ({ children }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { code: currCode, currencies, setCurrency } = useCurrency();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currOpen, setCurrOpen] = useState(false);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const data = await messagingAPI.getUnreadCount();
        setUnreadCount(data.unread || 0);
      } catch (e) { console.warn('Unread count fetch failed:', e.message); }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/watches', icon: Watch, label: 'Watches' },
    { path: '/brands', icon: Tag, label: 'Brands' },
    { path: '/categories', icon: FolderTree, label: 'Categories' },
    { path: '/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/users', icon: Users, label: 'Users' },
    { path: '/messages', icon: MessageSquare, label: 'Messages', badge: unreadCount },
    { path: '/settings', icon: Settings, label: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 h-14 border-b border-border bg-card/70 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="h-full px-4 flex items-center justify-between">
          <img src="/vwatch-logo.png" alt="vWatch Admin" className="w-28" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 border-r border-border bg-card transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-border">
            <img src="/vwatch-logo.png" alt="vWatch Admin" className="w-36" />
            <p className="text-xs text-muted-foreground mt-1">Luxury Watch Management</p>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto" aria-label="Main">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                <span className="text-sm font-medium flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold h-5 min-w-[20px] px-1.5 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/30">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-border">
            <Button
              onClick={() => {
                handleLogout();
                closeMobileMenu();
              }}
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start gap-3 text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 lg:w-72 fixed inset-y-0 left-0 border-r border-border bg-card/70 backdrop-blur supports-[backdrop-filter]:bg-card/60 flex-col">
        <div className="px-6 py-4 border-b border-border">
          <img src="/vwatch-logo.png" alt="vWatch Admin" className="w-32" />
          <p className="text-xs text-muted-foreground mt-1">Luxury Watch Management</p>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto" aria-label="Main">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200 ${
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              <span className="text-sm font-medium flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="bg-primary text-primary-foreground text-[10px] font-medium h-4 min-w-[16px] px-1 rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:bg-destructive/10"
          >
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:pl-64 lg:pl-72">
        <header className="hidden md:block sticky top-0 z-40 h-14 border-b border-border bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Admin Portal</h2>
            {/* Currency Selector */}
            <div className="relative">
              <button onClick={() => setCurrOpen(o => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-sm" data-testid="currency-selector">
                <span className="font-medium">{currCode}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {currOpen && (
                <>
                  <div className="fixed inset-0 z-[998]" onClick={() => setCurrOpen(false)} />
                  <div className="absolute right-0 top-10 z-[999] w-56 max-h-80 overflow-y-auto bg-white rounded-lg border border-gray-200 shadow-2xl py-1">
                    {currencies.map(c => (
                      <button key={c.code} onClick={() => { setCurrency(c.code); setCurrOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${c.code === currCode ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}>
                        <span>{c.symbol} {c.name}</span>
                        <span className="text-xs text-gray-400">{c.code}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-8 py-6">{children}</main>
      </div>
    </div>
  );
};

export default Layout;